// Client-side wrapper around /api/analyses for the persistent AI analysis
// cache. AIService.analyzeCreative consults this before invoking a model so
// the same creative is never re-analyzed across sessions or page reloads.

import { apiFetch } from "./apiClient";

export const PROMPT_VERSION = 1;

const queryString = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === "") return;
    usp.set(key, String(value));
  });
  const s = usp.toString();
  return s ? `?${s}` : "";
};

export const hashFile = async (file) => {
  if (!file) return null;
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch (error) {
    return null;
  }
};

export const getCachedAnalysis = async ({
  workspaceId,
  contentHash,
  model,
  creativeId,
  adId,
  promptVersion = PROMPT_VERSION,
}) => {
  if (!workspaceId || !contentHash || !model) return null;
  try {
    const data = await apiFetch(
      `/api/analyses${queryString({
        workspace_id: workspaceId,
        content_hash: contentHash,
        model,
        prompt_version: promptVersion,
        creative_id: creativeId,
        ad_id: adId,
      })}`,
      { method: "GET" },
    );
    return data?.row || null;
  } catch (error) {
    if (error?.status === 401 || error?.status === 403) return null;
    console.warn("analysisCache.getCachedAnalysis failed", error);
    return null;
  }
};

export const putCachedAnalysis = async ({
  workspaceId,
  contentHash,
  model,
  mediaKind,
  analysis,
  creativeId,
  adId,
  promptVersion = PROMPT_VERSION,
  conceptLabel,
  parentConceptId,
  confidence,
  citations,
  transcript,
}) => {
  if (!workspaceId || !contentHash || !model || !mediaKind) return null;
  try {
    const data = await apiFetch(
      `/api/analyses${queryString({ workspace_id: workspaceId })}`,
      {
        method: "POST",
        body: {
          workspace_id: workspaceId,
          content_hash: contentHash,
          model,
          media_kind: mediaKind,
          prompt_version: promptVersion,
          analysis,
          creative_id: creativeId,
          ad_id: adId,
          concept_label: conceptLabel,
          parent_concept_id: parentConceptId,
          confidence,
          citations,
          transcript,
        },
      },
    );
    return data?.row || null;
  } catch (error) {
    console.warn("analysisCache.putCachedAnalysis failed", error);
    return null;
  }
};

// Persist the AI creative-quality score for a GENERATED creative. Uses the same
// (workspace_id, content_hash, model, prompt_version) upsert key so re-scoring
// the same output overwrites rather than duplicating. `analysis` is required
// NOT NULL by the table, so we stash the review there too.
export const saveCreativeScore = async ({
  workspaceId,
  contentHash,
  model,
  mediaKind = "image",
  score,
  detail,
  promptVersion = PROMPT_VERSION,
}) => {
  if (!workspaceId || !contentHash || !model) return null;
  try {
    const data = await apiFetch(
      `/api/analyses${queryString({ workspace_id: workspaceId })}`,
      {
        method: "POST",
        body: {
          workspace_id: workspaceId,
          content_hash: contentHash,
          model,
          media_kind: mediaKind,
          prompt_version: promptVersion,
          analysis: { generated: true, review: detail || null },
          creative_score: score,
          score_detail: detail || null,
          scored_at: new Date().toISOString(),
        },
      },
    );
    return data?.row || null;
  } catch (error) {
    console.warn("analysisCache.saveCreativeScore failed", error);
    return null;
  }
};

export const listWorkspaceAnalyses = async ({ workspaceId }) => {
  if (!workspaceId) return [];
  try {
    const data = await apiFetch(
      `/api/analyses${queryString({ workspace_id: workspaceId, list: "1" })}`,
      { method: "GET" },
    );
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch (error) {
    console.warn("analysisCache.listWorkspaceAnalyses failed", error);
    return [];
  }
};

export const buildAnalysisIndex = (rows = []) => {
  const byCreative = new Map();
  const byAd = new Map();
  rows.forEach((row) => {
    if (!row) return;
    if (row.creative_id && !byCreative.has(row.creative_id)) {
      byCreative.set(row.creative_id, row);
    }
    if (row.ad_id) {
      byAd.set(row.ad_id, row);
    }
  });
  return { byCreative, byAd };
};

export const buildClusterEdges = (rows = []) => {
  const edges = [];
  const knownIds = new Set(rows.map((r) => r.id));
  rows.forEach((row) => {
    if (!row?.parent_concept_id) return;
    if (!knownIds.has(row.parent_concept_id)) return;
    edges.push({
      source: row.parent_concept_id,
      target: row.id,
      type: "parent",
    });
  });
  return edges;
};
